import { HostAppName, HostAppSettingName, HostAppSettings } from "../office-js/HostAppSettings";
import { OneNotePageError, OneNotePageErrors } from "./OneNotePageErrors";
import { ErrorStringFromException, getDateString, GetImageBuffer, isEmptyString, isNullOrUndefined, PromiseWithRetry } from "../../utils/utils";
import dispatcher from "../../frontend/dispatcher";
import { HostAppUtil } from "../office-js/HostAppUtil";
import { HostAppEventHandlerFactory } from "../factory/HostAppEventHandlerFactory";
import { HostAppEvent } from "../manager/eventManager/HostAppEventReceiver";
import { TranscriptionActionTypes } from "../../frontend/stores/utils";
import { ITranscriptionSourceFile } from "../interfaces/ITranscriptionSourceFileProvider";
import { AppLayoutSettings } from "../../frontend/common/styles/common.styles";
import { TranscriptionAlertActions, TranscriptionAlertButtonClickListener, TranscriptionAlertButtonType, TranscriptionAlertPriority, TranscriptionAlertState } from "../../frontend/components/Popups/TranscriptionAlert";
import { COMMA, EMPTY_STRING, LEARN_MORE_URL, LINKED_PAGE_INFO_LOAD_RETRY_DELAY_MILLISECONDS, MAX_LINKED_PAGE_INFO_LOAD_RETRY_ALLOWED, TRANSCRIPTION_SIGNAL_INDICATOR_PATH } from "../../AppConstants";
import { IAddTranscriptionIndicatorToHost } from "../interfaces/IAddTranscriptionIndicatorToHost";
import { HostAppEventHandler } from "../manager/eventManager/HostAppEventHandler";
import { LogError } from "../../utils/ErrorTelemetry";
import { LogEventNames } from "../interfaces/ITelemetryLogger";
import { OneNoteUserEventsKeys } from "../../frontend/common/telemetry/OneNoteEventsKeys";
import { LogEvents } from "../../utils/LogEvents";
import { EventCategory, EventDirection, EventType } from "../TelemetryLogger/LogEvent";
import { TranscriptionRoutePathNames } from "../../frontend/routes";
import { v4 as uuidv4 } from 'uuid';

export const OneNoteManagerActionTypes = {
  OneNoteAssociatedPageUpdate: 'ONE_NOTE_ASSOCIATED_PAGE_UPDATE'
};

class OneNoteTranscriptionManager implements IAddTranscriptionIndicatorToHost {

  private static notebookNameKey: string = 'name';
  private static sectionNameKey: string = 'name';
  private static pageNameKey: string = 'title';
  private static sectionGroupKey: string = 'parentSectionGroupOrNull';

  private associatedPageInfo: AssociatedPageInfo;

  private indicatorImageBuffer: string | ArrayBuffer | null = null;

  private history: any = null;

  private pageSwitchLiveRecordingIndicatorId: string = EMPTY_STRING;

  constructor() {
    this.associatedPageInfo = new AssociatedPageInfo();
  }

  public Initialize(history: any): void {
    LogEvents.OneNoteUserEvents.Start();
    this.history = history;
    const hostAppEventHandler: HostAppEventHandler = HostAppEventHandlerFactory.GetInstance();
    hostAppEventHandler.on(HostAppEvent[HostAppEvent.ONENOTE_PAGE_CHANGED], this.onPageChange);
    hostAppEventHandler.on(HostAppEvent[HostAppEvent.ONENOTE_PAGE_MOVED], this.onAssociatedPageMoved);
    hostAppEventHandler.on(HostAppEvent[HostAppEvent.ONENOTE_PAGE_DELETED], this.onPageDeleted);
    hostAppEventHandler.on(HostAppEvent[HostAppEvent.ONENOTE_NOTEBOOK_CLOSED], this.onNotebookClosed);

    /*Need to handle refresh case, delete the page and popup comes on screen, now right click on Transcription Pane and refresh, all intances are created again.
      also delay of 300 msec is added as sometime event was getting triggered before registerant register to dispatcher and event is missed.*/
    if (this.IsAssociatedPageAvailable()) {

      /*Retry attempt should be made for finite amount of time as page info is rquired
      for proper working of navigation to linked oneNote page.*/
      PromiseWithRetry(
        this.SyncAssociatedPageInfoFromOneNoteAndNotifyIfRequired,
        MAX_LINKED_PAGE_INFO_LOAD_RETRY_ALLOWED,
        LINKED_PAGE_INFO_LOAD_RETRY_DELAY_MILLISECONDS
      ).catch((e) => {
        LogError(LogEventNames.Warning, "Linked page info load failed with error " + ErrorStringFromException(e));
      });
    } else {
      setTimeout(() => { this.onPageDeleted(GetAssociatedPageId()) }, 300);
    }
  }

  public DeInitialize(): void {
    LogEvents.OneNoteUserEvents.End();
    const hostAppEventHandler: HostAppEventHandler = HostAppEventHandlerFactory.GetInstance();
    hostAppEventHandler.off(HostAppEvent[HostAppEvent.ONENOTE_PAGE_CHANGED], this.onPageChange);
    hostAppEventHandler.off(HostAppEvent[HostAppEvent.ONENOTE_PAGE_MOVED], this.onAssociatedPageMoved);
    hostAppEventHandler.off(HostAppEvent[HostAppEvent.ONENOTE_PAGE_DELETED], this.onPageDeleted);
    hostAppEventHandler.off(HostAppEvent[HostAppEvent.ONENOTE_NOTEBOOK_CLOSED], this.onNotebookClosed);
  }

  public async AddTranscriptionIndicator(sourceFile: ITranscriptionSourceFile): Promise<void> {
    if (isNullOrUndefined(sourceFile)) {
      throw new OneNotePageError(OneNotePageErrors.AddTranscriptionIndicatorToOneNoteFailure, "can not add indicator for null or underfined ITranscriptionSourceFile");
    }

    // If indicatorImageBuffer is not available, generate indicatorImageBuffer.
    if (isNullOrUndefined(this.indicatorImageBuffer)) {
      try {
        this.indicatorImageBuffer = await GetImageBuffer(TRANSCRIPTION_SIGNAL_INDICATOR_PATH);
      } catch (e) {
        throw new OneNotePageError(OneNotePageErrors.AddTranscriptionIndicatorToOneNoteFailure, "failed to fetch image buffer");
      }
    }

    // create Trancription Indicator html and post to oneNote Page, which is linked with Transcription pane.
    try {
      const audioNameWithURL = "<a href=" + sourceFile?.permanentAudioUrl + ">" + sourceFile!.fileName + "</a>";
      const information = audioNameWithURL + ' ' + TranscriptionStrings.OneNotePageIndicatorText;

      const html = "<img src=" + this.indicatorImageBuffer + "width=\"30\" height=\"30\"> </img>" +
        "<p style = color:" + AppLayoutSettings.getAppColorContainer().transcriptionIndicatorTextColor.value + ";" + "font-size:" + AppLayoutSettings.AppFontSize.mediumFontSizePx + ";" + ">" + information + "</p>" +
        "<p style= color:" + AppLayoutSettings.getAppColorContainer().transcriptionIndicatorTextColor.value + ";" + "font-size:" + AppLayoutSettings.AppFontSize.mediumFontSizePx + ";" + ">" + getDateString() + "</p>"

      // Post the html content to OneNote page, linked with transcription
      await OneNote.run(async (context) => {
        await NavigateToAndFetchAssociatedPage(context);
        context.application.insertHtmlAtCurrentPosition(html);
        context.sync();
      });
    } catch (e) {
      throw new OneNotePageError(OneNotePageErrors.AddTranscriptionIndicatorToOneNoteFailure, ErrorStringFromException(e));
    }
  }

  private onPageChange = (oldPageId: string, newPageId: string) => {
    /*During page Delete, Page change event is also triggered from one
    note as context move to other available page, but we need to discard same*/
    if (!this.IsAssociatedPageAvailable()) {
      return;
    }
    const associatedPageId = GetAssociatedPageId();

    if (newPageId == associatedPageId || oldPageId == associatedPageId) {
      this.SyncAssociatedPageInfoFromOneNoteAndNotifyIfRequired().catch((e) => {
        LogError(LogEventNames.Warning, "Linked page info load failed during Page Change with error " + ErrorStringFromException(e));
      });
    }

    //user navigate back to linked onenote page.
    if (newPageId == associatedPageId) {
      if (this.history.location.pathname === TranscriptionRoutePathNames.Recording) {
        this.removePageSwitchLiveRecordingIndicator();
      }

      dispatcher.dispatch({
        type: TranscriptionActionTypes.ShareCurrentTranscriptionState
      });
    }

    //user navigate from linked onenote page to some other onenote page.
    else if (oldPageId == associatedPageId) {
      if (this.history.location.pathname === TranscriptionRoutePathNames.Recording) {
        this.showPageSwitchLiveRecordingIndicator();
      }
    }

    LogEvents.OneNoteUserEvents.FlushEventAdhoc(
      [{ key: OneNoteUserEventsKeys.TranscriptionSessionId, value: HostAppSettings.GetSettingAsString(HostAppSettingName.TranscriptionSessionId) },
      { key: OneNoteUserEventsKeys.EventCategory, value: EventCategory.CanvasEvent },
      { key: OneNoteUserEventsKeys.EventType, value: EventType.PageChange },
      { key: OneNoteUserEventsKeys.EventDirection, value: EventDirection.Received },
      { key: OneNoteUserEventsKeys.OneNotePageId, value: newPageId },
      { key: OneNoteUserEventsKeys.TranscriptionState, value: this.history.location.pathname }]);
  }

  private showPageSwitchLiveRecordingIndicator() {

    // precautionary check to prevent multi popup inflation.
    if (!isNullOrUndefined(this.pageSwitchLiveRecordingIndicatorId)) {
      return;
    }

    /* Transcription Alert Id to be used later to remove dialog,
       when user is back to linked page.*/
    this.pageSwitchLiveRecordingIndicatorId = uuidv4();

    const listener: TranscriptionAlertButtonClickListener = <TranscriptionAlertButtonClickListener>({

      //Transcription Alert button click event handling.
      onButtonClicked(buttonType: TranscriptionAlertButtonType): void {
        if (buttonType == TranscriptionAlertButtonType.LINK) {
          NavigateToAssociatedPage().catch((e) => {
            LogError(LogEventNames.Warning,
              "Navigation to linked page failed during  link click on InkingStrokesNotGettingCapturedPopup with error " +
              ErrorStringFromException(e));
          });
        }
      }
    });

    const transcriptionAlertState: TranscriptionAlertState = {
      title: TranscriptionStrings.InkingStrokesNotCapturingPopupTitle,
      information: TranscriptionStrings.InkingStrokesNotCapturingPopupText + '\n' + TranscriptionStrings.InkingStrokesNotCapturingPopupSubText,
      buttonInfo: {
        buttonType: TranscriptionAlertButtonType.LINK |
          TranscriptionAlertButtonType.PRIMARY_BUTTON,
        buttonClickListener: listener,
        primaryButtonText: TranscriptionStrings.FileRecoveryGotItButtonLabel,
        linkText: TranscriptionStrings.InkingStrokesNotCapturingPopupLinkLabel,
      },
      id: this.pageSwitchLiveRecordingIndicatorId
    }

    dispatcher.dispatch({
      type: TranscriptionAlertActions.ShowTranscriptionAlert,
      payload: transcriptionAlertState
    });
  }


  private removePageSwitchLiveRecordingIndicator() {
    if (!isNullOrUndefined(this.pageSwitchLiveRecordingIndicatorId)) {
      dispatcher.dispatch({
        type: TranscriptionAlertActions.RemoveTranscriptionAlert,
        payload: this.pageSwitchLiveRecordingIndicatorId
      });
      this.pageSwitchLiveRecordingIndicatorId = EMPTY_STRING;
    }
  }

  private onPageDeleted = (pageId: string) => {
    if (pageId == GetAssociatedPageId()) {
      this.handleLinkedPageNotAvailable();
    }

    LogEvents.OneNoteUserEvents.FlushEventAdhoc(
      [{ key: OneNoteUserEventsKeys.TranscriptionSessionId, value: HostAppSettings.GetSettingAsString(HostAppSettingName.TranscriptionSessionId) },
      { key: OneNoteUserEventsKeys.EventCategory, value: EventCategory.CanvasEvent },
      { key: OneNoteUserEventsKeys.EventType, value: EventType.PageDelete },
      { key: OneNoteUserEventsKeys.EventDirection, value: EventDirection.Received },
      { key: OneNoteUserEventsKeys.OneNotePageId, value: pageId },
      { key: OneNoteUserEventsKeys.TranscriptionState, value: this.history.location.pathname }]);
  }

  private onNotebookClosed = (notebookId: string) => {
    if (notebookId == GetAssociatedNotebookId()) {
      this.handleLinkedPageNotAvailable();
    }

    LogEvents.OneNoteUserEvents.FlushEventAdhoc(
      [{ key: OneNoteUserEventsKeys.TranscriptionSessionId, value: HostAppSettings.GetSettingAsString(HostAppSettingName.TranscriptionSessionId) },
      { key: OneNoteUserEventsKeys.EventCategory, value: EventCategory.CanvasEvent },
      { key: OneNoteUserEventsKeys.EventType, value: EventType.NotebookClose },
      { key: OneNoteUserEventsKeys.EventDirection, value: EventDirection.Received },
      { key: OneNoteUserEventsKeys.OneNoteNotebookId, value: notebookId },
      { key: OneNoteUserEventsKeys.TranscriptionState, value: this.history.location.pathname }]);
  }

  private handleLinkedPageNotAvailable() {
    this.DeInitialize();
    HostAppSettings.Set(HostAppSettingName.IsAssociatedPageDeleted, true);

    //Show Info dialog that associated page is no more available.
    const transcriptionAlertState: TranscriptionAlertState = {
      information: TranscriptionStrings.OneNotePageNotAvailableMessage,
      title: TranscriptionStrings.OneNotePageNotAvailableTitle,
      buttonInfo: { buttonType: TranscriptionAlertButtonType.NONE },
      priority: TranscriptionAlertPriority.CRITICAL
    }

    dispatcher.dispatch({
      type: TranscriptionAlertActions.ShowTranscriptionAlert,
      payload: transcriptionAlertState
    });
  }

  //This method should be called only in case Associated page, section has been moved to different section or notebook.
  private onAssociatedPageMoved = (newPageId: string, newSectionId: string, newNotebookId: string, newPageHyperLink: string) => {
    HostAppSettings.Set(HostAppSettingName.AssociatedOneNotePageId, newPageId);
    HostAppSettings.Set(HostAppSettingName.AssociatedOneNoteSectionId, newSectionId);
    HostAppSettings.Set(HostAppSettingName.AssociatedOneNoteNotebookId, newNotebookId);
    HostAppSettings.Set(HostAppSettingName.AssociatedOneNotePageHyperLink, newPageHyperLink);
    this.SyncAssociatedPageInfoFromOneNoteAndNotifyIfRequired().catch((e) => {
      LogError(LogEventNames.Warning, "Linked page info load failed after page move with error " + ErrorStringFromException(e));
    });

    LogEvents.OneNoteUserEvents.FlushEventAdhoc(
      [{ key: OneNoteUserEventsKeys.TranscriptionSessionId, value: HostAppSettings.GetSettingAsString(HostAppSettingName.TranscriptionSessionId) },
      { key: OneNoteUserEventsKeys.EventCategory, value: EventCategory.CanvasEvent },
      { key: OneNoteUserEventsKeys.EventType, value: EventType.PageMove },
      { key: OneNoteUserEventsKeys.EventDirection, value: EventDirection.Received },
      { key: OneNoteUserEventsKeys.OneNotePageId, value: newPageId },
      { key: OneNoteUserEventsKeys.OneNoteSectionId, value: newSectionId },
      { key: OneNoteUserEventsKeys.OneNoteNotebookId, value: newNotebookId },
      { key: OneNoteUserEventsKeys.TranscriptionState, value: this.history.location.pathname }]);
  }

  /* This method sync the latest oneNote page, section and notebook name,
  and dispatch an event in case of any change*/
  private SyncAssociatedPageInfoFromOneNoteAndNotifyIfRequired = async () => {
    const associatedPageInfoUpdated = await this.LoadLatestInfoForAssociatedPage();
    if (associatedPageInfoUpdated) {

      //Notify listeners as OneNote page info has changed.
      dispatcher.dispatch({
        type: OneNoteManagerActionTypes.OneNoteAssociatedPageUpdate,
      });
    }
  }

  private async LoadLatestInfoForAssociatedPage(): Promise<boolean> {
    let associatedPageInfoUpdated: boolean = false;
    await OneNote.run(async (context) => {

      let linkedPage: OneNote.Page | null = null;
      let linkedNotebook: OneNote.Notebook | null = null;
      let linkedSection: OneNote.Section | null = null;
      let linkedPageFound = false;

      //check if active page is same linked page.
      try {
        const activePage = context.application.getActivePageOrNull();
        activePage.load('id');
        await context.sync();
        if (activePage.id == GetAssociatedPageId()) {
          linkedPage = activePage;
          linkedNotebook = context.application.getActiveNotebookOrNull();
          linkedSection = context.application.getActiveSectionOrNull();
          linkedPageFound = true;
        }
      } catch (e) { linkedPageFound = false; }

      // If linked page is not active one, look through the linkednotebook.
      if (!linkedPageFound) {
        linkedNotebook = context.application.notebooks.getItem(GetAssociatedNotebookId());

        // check for section group and fetch linkedSection.
        linkedSection = await GetSectionFromSectionGroup(linkedNotebook, context);

        // get linkedSection from linkedNotebook as no parent section group is attached to linked section.
        if (isNullOrUndefined(linkedSection)) {
          linkedSection = linkedNotebook.sections.getItem(GetAssociatedSectionId());
        }
        if (!isNullOrUndefined(linkedSection)) {
          linkedPage = linkedSection!.pages.getItem(GetAssociatedPageId());
        }
      }

      //Load notebook, section and page name.
      try {
        if (!isNullOrUndefined(linkedNotebook)) {
          linkedNotebook!.load(OneNoteTranscriptionManager.notebookNameKey);
        }
        if (!isNullOrUndefined(linkedSection)) {

          //load both section name and section Group
          linkedSection!.load(OneNoteTranscriptionManager.sectionNameKey + COMMA + OneNoteTranscriptionManager.sectionGroupKey);
        }
        if (!isNullOrUndefined(linkedPage)) {
          linkedPage!.load(OneNoteTranscriptionManager.pageNameKey);
        }
        await context.sync();
        linkedPageFound = true;
      } catch (e) {
        linkedPageFound = false;
        throw new OneNotePageError(OneNotePageErrors.LoadLatestInfoForAssociatedPageFailure, ErrorStringFromException(e));
      }

      //Read the loaded information to pageInfo object.
      if (linkedPageFound) {
        if (!isNullOrUndefined(linkedNotebook)) {
          if (linkedNotebook!.name != this.associatedPageInfo.notebookName) {
            this.associatedPageInfo.notebookName = linkedNotebook!.name;
            associatedPageInfoUpdated = true;
          }
        }

        if (!isNullOrUndefined(linkedSection)) {
          if (linkedSection!.name != this.associatedPageInfo.sectionName) {
            this.associatedPageInfo.sectionName = linkedSection!.name;
            associatedPageInfoUpdated = true;
          }

          //check for parent section grp of linkedsection
          const linkedSectionGrp = linkedSection!.parentSectionGroupOrNull;
          let linkedSectionGrpId = EMPTY_STRING;
          if (!isNullOrUndefined(linkedSectionGrp)) {
            linkedSectionGrp.load('isNullObject, id');
            await context.sync();
            if (!linkedSectionGrp.isNullObject) {
              linkedSectionGrpId = isNullOrUndefined(linkedSectionGrp.id) ? EMPTY_STRING : linkedSectionGrp.id;
            }
          }
          if (linkedSectionGrpId != this.associatedPageInfo.sectionGroupId) {
            this.associatedPageInfo.sectionGroupId = linkedSectionGrpId;
            associatedPageInfoUpdated = true;
          }
        }

        if (!isNullOrUndefined(linkedPage)) {
          if (linkedPage!.title != this.associatedPageInfo.pageName) {
            this.associatedPageInfo.pageName = linkedPage!.title;
            associatedPageInfoUpdated = true;
          }
        }
      }
    });
    return associatedPageInfoUpdated;
  }

  public GetAssociatedNoteBookName() {
    return this.associatedPageInfo.notebookName; //associatedNoteBookName
  }

  public async RefreshPageInfoRequest() {
    return new Promise<void>(function (resolve, reject) {
      oneNoteTranscriptionManager.SyncAssociatedPageInfoFromOneNoteAndNotifyIfRequired().then(() => {
        resolve();
      }).catch((e) => {
        reject(e);
      });
    });
  }

  public GetAssociatedSectionName() {
    return this.associatedPageInfo.sectionName; //associatedSectionName
  }

  public GetAssociatedPageName() {
    return this.associatedPageInfo.pageName; //associatedPageName
  }

  public GetAssociatedSectiongroupId() {
    return this.associatedPageInfo.sectionGroupId;
  }

  public IsAssociatedPageAvailable = () => {
    return !HostAppSettings.IsSettingTrue(HostAppSettingName.IsAssociatedPageDeleted);
  }
}

class AssociatedPageInfo {

  notebookName: string;
  sectionName: string;
  pageName: string;
  sectionGroupId: string;

  constructor(notebookName: string = '', sectionName: string = '', pageName: string = '', sectionGroupId: string = '') {
    this.notebookName = notebookName;
    this.sectionName = sectionName;
    this.pageName = pageName;
    this.sectionGroupId = sectionGroupId;
  }
}

/* returns Active Page object if Active page id is same as Assocaited page id,
   else return null*/
export const GetActivePageIfSameAsAssociatedPage = async (context: OneNote.RequestContext): Promise<OneNote.Page | null> => {
  return new Promise<OneNote.Page | null>(async function (resolve) {
    try {
      const activePage = context.application.getActivePageOrNull();
      activePage.load('id');
      await context.sync();
      if (activePage.id == GetAssociatedPageId()) {
        return resolve(activePage);
      } else {
        return resolve(null);
      }
    } catch (e) {
      return resolve(null);
    }
  });
}

export const NavigateToAssociatedPage = async (): Promise<void> => {
  await OneNote.run(async (context) => {
    NavigateToAndFetchAssociatedPage(context).then(() => {
    }).catch((e) => {
      throw e;
    });
  })
}

export const NavigateToAndFetchAssociatedPage = async (context: OneNote.RequestContext): Promise<OneNote.Page | null> => {
  return new Promise<OneNote.Page | null>(async function (resolve, reject) {
    const activePage = await GetActivePageIfSameAsAssociatedPage(context);
    if (!isNullOrUndefined(activePage)) {
      return resolve(activePage);
    }

    const LinkedPageClientUrl = HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageHyperLink);
    try {
      if (!isEmptyString(LinkedPageClientUrl)) {

        // Open the page on OneNote side via clientUrl.
        await context.application.navigateToPageWithClientUrl(LinkedPageClientUrl);
      }
      else {
        const notebook = context.application.notebooks.getItem(GetAssociatedNotebookId());

        //fetch section via secton group or notebook, in case no section group available.
        let section = await GetSectionFromSectionGroup(notebook, context);
        if (isNullOrUndefined(section)) {
          section = notebook.sections.getItem(GetAssociatedSectionId());
        }

        //fetch page via secion.
        const page = section!.pages.getItem(GetAssociatedPageId());

        // Open the page on OneNote side via page object.
        await context.application.navigateToPage(page);
      }
      const activePage = await GetActivePageIfSameAsAssociatedPage(context);
      if (!isNullOrUndefined(activePage)) {
        return resolve(activePage);
      } else {
        return reject(new OneNotePageError(OneNotePageErrors.NavigateToAssociatedPageFailure,
          "Navigation failed active page not same as assocaited page."));
      }

    } catch (e) {
      return reject(new OneNotePageError(OneNotePageErrors.NavigateToAssociatedPageFailure,
        ErrorStringFromException(e)));
    }
  });
}

export const GetSectionFromSectionGroup = async (linkedNotebook: OneNote.Notebook, context: OneNote.RequestContext): Promise<OneNote.Section | null> => {
  if (isEmptyString(GetAssociatedSectionGroupId()) || isNullOrUndefined(linkedNotebook) ||
    isNullOrUndefined(context)) {
    return null;
  }
  let sectiongrpCollection: OneNote.SectionGroupCollection | undefined = linkedNotebook.sectionGroups;
  let linkedSection: OneNote.Section | null = null;
  let sectionGrpCollectionQueue: Array<OneNote.SectionGroupCollection> = new Array<OneNote.SectionGroupCollection>();
  if (!isNullOrUndefined(sectiongrpCollection)) {
    sectionGrpCollectionQueue.push(sectiongrpCollection);
  }
  while (sectionGrpCollectionQueue.length > 0) {
    try {
      sectiongrpCollection = sectionGrpCollectionQueue.shift();
      sectiongrpCollection?.load('count, isNullObject');
      await context.sync();
      if (sectiongrpCollection!.isNullObject || sectiongrpCollection!.count <= 0) {
        continue;
      }
      const linkedSectiongrp = sectiongrpCollection!.getItem(GetAssociatedSectionGroupId());
      linkedSectiongrp.load('isNullObject');

      /* context.Sync() will fail, when assocaited Section group id from
      GetAssociatedSectionGroupId() is not avaiable in sectiongrpCollection and
      flow will move to catch block*/
      await context.sync();
      if (!linkedSectiongrp.isNullObject) {
        linkedSection = linkedSectiongrp.sections.getItem(GetAssociatedSectionId());
      }
      break;
    } catch (e) {
      for (let i = 0; i < sectiongrpCollection!.count; i++) {
        const childSectionGrpCollection = sectiongrpCollection!.getItem(i).sectionGroups;
        if (!isNullOrUndefined(childSectionGrpCollection)) {
          sectionGrpCollectionQueue.push(childSectionGrpCollection);
        }
      }
    }
  }
  return linkedSection;
}

export const InitializeOneNoteTranscriptionManager = (history: any) => {
  oneNoteTranscriptionManager.Initialize(history);
}

export const ShouldShowOneNotePageInfoBar = () => {
  //only show this bar for oneNote.
  return HostAppUtil.GetHostAppName() === HostAppName.ONENOTE;
}

export const IsOneNoteNotebookIdentityEmpty = () => {
  return isNullOrUndefined(HostAppSettings.GetSettingAsString(HostAppSettingName.OneNoteNotebookIdentityEmail))
    || (HostAppSettings.GetSettingAsString(HostAppSettingName.OneNoteNotebookIdentityEmail) == EMPTY_STRING);
}

export const ShouldShowOneNoteIdentitySwitchDialog = (primaryIdentityEmail: string | undefined) => {
  const shouldShowOneNoteIdentitySwitchDialog = !IsOneNoteNotebookIdentityEmpty()
    && (primaryIdentityEmail != HostAppSettings.GetSettingAsString(HostAppSettingName.OneNoteNotebookIdentityEmail));
  return shouldShowOneNoteIdentitySwitchDialog;
}

export const ShowOneNoteIdentitySwitchDialog = (primaryIdentityEmail: string | undefined) => {
  const associatedNoteBookName = oneNoteTranscriptionManager.GetAssociatedNoteBookName();
  const activeIdentityEmail = isNullOrUndefined(primaryIdentityEmail) ? EMPTY_STRING : primaryIdentityEmail;
  const notebookIdentityEmail = isNullOrUndefined(HostAppSettings.GetSettingAsString(HostAppSettingName.OneNoteNotebookIdentityEmail))
    ? EMPTY_STRING : HostAppSettings.GetSettingAsString(HostAppSettingName.OneNoteNotebookIdentityEmail);

  let identitySwitchDialogInfoString = TranscriptionStrings.OneNoteAccountSwitchDialogBodyInfoText;
  identitySwitchDialogInfoString = identitySwitchDialogInfoString.replace('{0}', '[' + activeIdentityEmail + ']');
  identitySwitchDialogInfoString = identitySwitchDialogInfoString.replace('{1}', '\'' + associatedNoteBookName
    + '\'');
  identitySwitchDialogInfoString = identitySwitchDialogInfoString.replace('{2}', '[' + notebookIdentityEmail + ']');

  const transcriptionAlertState: TranscriptionAlertState = {
    title: TranscriptionStrings.OneNoteAccountSwitchDialogHeaderText,
    information: identitySwitchDialogInfoString + '\n\n' + TranscriptionStrings.AccountSwitchDialogBodyRelaunchText,
    buttonInfo: { buttonType: TranscriptionAlertButtonType.LINK, linkText: TranscriptionStrings.TranscriptionPaneLearnLabel, hyperLink: LEARN_MORE_URL }
  }

  dispatcher.dispatch({
    type: TranscriptionAlertActions.ShowTranscriptionAlert,
    payload: transcriptionAlertState
  });
}

export function GetAssociatedPageId(): string {
  return HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId);
}

export function GetAssociatedNotebookId(): string {
  return HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNoteNotebookId);
}

export function GetAssociatedSectionId(): string {
  return HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNoteSectionId);
}

export function GetAssociatedSectionGroupId(): string {
  return oneNoteTranscriptionManager.GetAssociatedSectiongroupId();
}

const oneNoteTranscriptionManager = new OneNoteTranscriptionManager();
export default oneNoteTranscriptionManager;